import Image from 'next/image'
import { Icon } from '@captaincause/ui/Icon'
import { tm } from '@captaincause/ui/tm'
import { flattenImage } from 'utils/strapi/image'
import { Button } from 'components/Button'
import { Markdown } from 'components/Markdown'
import { StrapiImage } from 'components/StapiImage'
import type { ButtonProps } from 'components/Button'
import type { LocalImage, StrapiApiImage } from 'types/strapi/image'
import type { StrapiLink } from 'types/strapi/link'

export interface ZigZagCheckItem {
  id: number
  title: string | null
  subtitle?: string | null
  description: string | null
  CTAIntroduction?: string | null
  link: (StrapiLink & { locale?: ButtonProps['locale'] }) | null
  image: StrapiApiImage | LocalImage
}

export interface ZigZagCheckProps {
  zigzagcheckItems: ZigZagCheckItem[] | null
  firstImageLeft?: boolean
}

export default function ZigZagCheck({
  zigzagcheckItems,
  firstImageLeft = true,
}: ZigZagCheckProps) {
  return (
    !!zigzagcheckItems &&
    zigzagcheckItems.map((item, index) => {
      const image = 'data' in item.image ? flattenImage(item.image) : item.image
      const rows = item.description
        ?.split('\n')
        .map(row => row.trim())
        .filter(Boolean)
      const isEven = firstImageLeft ? index % 2 === 0 : !(index % 2 === 0)
      const isLast = index === zigzagcheckItems.length - 1

      if (rows?.length === 0) {
        return null
      }

      return (
        <div key={item.id} className="bg-white">
          <div
            className={tm(
              'm-auto flex w-full max-w-[1400px] flex-col items-center justify-center gap-8 overflow-hidden px-4 py-10 md:px-20 md:py-14 lg:flex-row-reverse lg:gap-6 lg:py-20',
              isEven && 'lg:flex-row',
            )}
          >
            {image ? (
              <div>
                {'url' in image ? (
                  <StrapiImage
                    {...image}
                    className="w-full max-w-[450px] object-contain md:w-fit md:object-cover"
                  />
                ) : (
                  <Image
                    src={image.image}
                    height={250}
                    width={400}
                    alt={image.alt}
                    className="w-full max-w-[450px] object-contain md:w-fit md:object-cover"
                  />
                )}
              </div>
            ) : null}
            <div className="flex flex-col items-center gap-6 lg:items-start lg:px-14">
              {item.title ? (
                <h3 className="font-default text-[40px] font-semibold leading-[48px] -tracking-[1.5px]">
                  {item.title}
                </h3>
              ) : null}
              {item.subtitle ? (
                <p className="font-default text-lg font-normal leading-7 text-dark-gray">
                  {item.subtitle}
                </p>
              ) : null}
              <ul className="flex flex-col gap-2 pl-4">
                {rows?.map(content => {
                  if (!content) return null

                  return (
                    <li key={content} className="flex list-none items-start">
                      <div className="mr-3 rounded-full bg-light-purple p-1">
                        <Icon icon="check" fill="fill-dark-purple" />
                      </div>
                      <Markdown className="text-lg font-normal leading-7 text-dark-gray">
                        {content}
                      </Markdown>
                    </li>
                  )
                })}
              </ul>
              {item.CTAIntroduction ? (
                <p className="font-default text-lg font-normal leading-7 text-dark-gray">
                  {item.CTAIntroduction}
                </p>
              ) : null}
              {item.link?.url ? (
                <div className="pl-4">
                  <Button
                    href={item.link.url}
                    variant="secondary"
                    className="rounded-[40px] border-gray px-7 py-3"
                    locale={item.link.locale}
                  >
                    {item.link.title}
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
          <hr
            className={tm(
              'h-[1px] border-none bg-gray lg:mx-20',
              isLast && 'hidden',
            )}
          />
        </div>
      )
    })
  )
}
